// 最近更新 (D-222): the words of src/updates/i18n/<locale>.json, loaded on demand (one small chunk per language), for
// the public /updates page, the homepage block and the 功能更新 tab of the teacher's notices.
import { useEffect, useState } from "react";
import { msg } from "../../src/i18n/translate";
import { publishedUpdates, type UpdateEntry, type UpdatePageCopy, type UpdateTheme, type UpdateText } from "../../src/updates/feed";
import type { ForestIconName } from "./app-core";
import { locale, t, type Locale } from "./i18n";

export type UpdateCopy = Record<string, UpdateText> & { _page?: UpdatePageCopy & { intro?: string } };
const loaders = import.meta.glob<{ default: UpdateCopy }>("../../src/updates/i18n/*.json");
const cache = new Map<Locale, Promise<UpdateCopy>>();

export function loadUpdateCopy(target: Locale = locale): Promise<UpdateCopy> {
  let pending = cache.get(target);
  if (!pending) {
    const loader = loaders[`../../src/updates/i18n/${target}.json`] ?? loaders["../../src/updates/i18n/zh-Hant.json"];
    pending = loader().then((module) => module.default);
    pending.catch(() => cache.delete(target));
    cache.set(target, pending);
  }
  return pending;
}

/** The copy of the teacher's language, or null while it loads (or if it cannot). */
export function useUpdateCopy(active = true): UpdateCopy | null {
  const [copy, setCopy] = useState<UpdateCopy | null>(null);
  useEffect(() => {
    if (!active || copy) return;
    let live = true;
    loadUpdateCopy().then((value) => { if (live) setCopy(value); }, () => {});
    return () => { live = false; };
  }, [active, copy]);
  return copy;
}

/** Entries live today that have words in this language, newest first. */
export const liveUpdates = (copy: UpdateCopy | null, now = new Date()): UpdateEntry[] =>
  copy ? publishedUpdates(now).filter((entry) => copy[entry.id]) : [];

/** Theme names, in page order. */
export const UPDATE_THEMES: ReadonlyArray<{ id: UpdateTheme; label: string; icon: ForestIconName }> = [
  { id: "homework", label: msg("作業"), icon: "homework" },
  { id: "student-links", label: msg("學生專屬連結"), icon: "students" },
  { id: "pets", label: msg("寵物成長"), icon: "paw" },
  { id: "classroom", label: msg("上課好用"), icon: "star" },
  { id: "contact", label: msg("聯絡簿"), icon: "journal" },
  { id: "rewards", label: msg("獎勵兌換"), icon: "gift" },
  { id: "tools", label: msg("免費工具"), icon: "stopwatch" },
  { id: "account", label: msg("通知與方案"), icon: "bell" },
];
export const themeLabel = (theme: UpdateTheme) => t(UPDATE_THEMES.find((item) => item.id === theme)?.label ?? "");
export const updateImage = (entry: UpdateEntry) => entry.image ? `/assets/changelog/${entry.image}.webp` : null;
/** Screenshots are all captured at this size (scripts/capture-changelog.mjs). */
export const UPDATE_IMAGE_SIZE = { width: 1200, height: 750 } as const;
